import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { inject } from '@angular/core';

import { EventService } from './event.service';
import { AppEvent, EventRegistration } from './models';

@Component({
    standalone: false,
    selector: 'abs-event-manage-registrations',
    template: `
        @if (event$ | async; as event) {
            <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.REGISTRATION.MANAGE_TITLE' | translate">
                @if (registrations$ | async; as registrations) {
                    @if (pendingRegistrations(registrations).length > 0) {
                        <div class="reg-group">
                            <h4 class="sub-title">
                                {{ 'GLOBAL.EVENTS.REGISTRATION.PENDING' | translate }}
                                ({{ pendingRegistrations(registrations).length }})
                            </h4>
                            <ion-list lines="full">
                                @for (reg of pendingRegistrations(registrations); track reg._id) {
                                    <ion-item>
                                        <ion-label>
                                            <h3>{{ reg.displayName }}</h3>
                                            @if (reg.registeredAt) {
                                                <p>{{ reg.registeredAt | date: 'mediumDate' }}</p>
                                            }
                                        </ion-label>
                                        <ion-button
                                            class="btn-sm btn-icon"
                                            color="success"
                                            slot="end"
                                            (click)="approveRegistration(event, reg)"
                                        >
                                            <ion-icon name="checkmark-outline" slot="icon-only"></ion-icon>
                                        </ion-button>
                                        <ion-button
                                            class="btn-sm btn-icon"
                                            color="danger"
                                            slot="end"
                                            (click)="removeRegistration(event, reg)"
                                        >
                                            <ion-icon name="close-outline" slot="icon-only"></ion-icon>
                                        </ion-button>
                                    </ion-item>
                                }
                            </ion-list>
                        </div>
                    }
                    @if (activeRegistrations(registrations).length > 0) {
                        <div class="reg-group">
                            <h4 class="sub-title">
                                {{ 'GLOBAL.EVENTS.REGISTRATION.ACTIVE' | translate }}
                                ({{ activeRegistrations(registrations).length }})
                            </h4>
                            <ion-list lines="full">
                                @for (reg of activeRegistrations(registrations); track reg._id) {
                                    <ion-item>
                                        <ion-label>
                                            <h3>{{ reg.displayName }}</h3>
                                            @if (reg.registeredAt) {
                                                <p>{{ reg.registeredAt | date: 'mediumDate' }}</p>
                                            }
                                        </ion-label>
                                        <ion-button
                                            class="btn-sm btn-icon"
                                            color="danger"
                                            fill="clear"
                                            slot="end"
                                            (click)="removeRegistration(event, reg)"
                                        >
                                            <ion-icon name="remove-circle-outline" slot="icon-only"></ion-icon>
                                        </ion-button>
                                    </ion-item>
                                }
                            </ion-list>
                        </div>
                    }
                    @if (waitlistRegistrations(registrations).length > 0) {
                        <div class="reg-group">
                            <h4 class="sub-title">
                                {{ 'GLOBAL.EVENTS.REGISTRATION.WAITLISTED' | translate }}
                                ({{ waitlistRegistrations(registrations).length }})
                            </h4>
                            <ion-list lines="full">
                                @for (reg of waitlistRegistrations(registrations); track reg._id) {
                                    <ion-item>
                                        <ion-label>
                                            <h3>{{ reg.displayName }}</h3>
                                            @if (reg.registeredAt) {
                                                <p>{{ reg.registeredAt | date: 'mediumDate' }}</p>
                                            }
                                        </ion-label>
                                        <ion-button
                                            class="btn-sm btn-icon"
                                            color="success"
                                            slot="end"
                                            (click)="approveRegistration(event, reg)"
                                        >
                                            <ion-icon name="checkmark-outline" slot="icon-only"></ion-icon>
                                        </ion-button>
                                    </ion-item>
                                }
                            </ion-list>
                        </div>
                    }
                    @if (registrations.length === 0) {
                        <div class="empty-state">
                            <ion-icon name="people-outline"></ion-icon>
                            <p>{{ 'GLOBAL.EVENTS.REGISTRATION.NO_REGISTRATIONS' | translate }}</p>
                        </div>
                    }
                }
            </abs-page-layout>
        }
    `,
    styles: [
        `
            .reg-group {
                padding: 0 16px;
            }

            .sub-title {
                margin: 16px 0 4px;
                font-size: 0.8rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                color: var(--ion-color-medium, #92949c);
            }

            .reg-group ion-list {
                margin: 0;
                padding: 0;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px 16px;
                color: var(--ion-color-medium, #92949c);
            }

            .empty-state ion-icon {
                font-size: 3rem;
                margin-bottom: 12px;
            }

            .empty-state p {
                margin: 0;
                font-size: 0.95rem;
            }
        `
    ]
})
export class EventManageRegistrationsPage implements OnInit {
    private eventService = inject(EventService);
    private route = inject(ActivatedRoute);

    event$: Observable<AppEvent>;
    registrations$: Observable<EventRegistration[]>;

    private refreshRegistrations$ = new BehaviorSubject<void>(undefined);

    ngOnInit() {
        this.event$ = this.route.paramMap.pipe(
            map((params) => params.get('eventId')),
            switchMap((id) => this.eventService.getEvent(id))
        );

        this.registrations$ = combineLatest([this.event$, this.refreshRegistrations$]).pipe(
            switchMap(([event]) => {
                if (!event?._id) return of([]);
                return this.eventService.listRegistrations(event._id).pipe(catchError(() => of([])));
            }),
            map((regs) => regs.filter((r) => r.status !== 'withdrawn' && r.status !== 'removed'))
        );
    }

    approveRegistration(event: AppEvent, reg: EventRegistration) {
        this.eventService.updateRegistrationStatus(event._id, reg._id, 'active').subscribe(() => {
            this.refreshRegistrations$.next();
        });
    }

    removeRegistration(event: AppEvent, reg: EventRegistration) {
        this.eventService.updateRegistrationStatus(event._id, reg._id, 'removed').subscribe(() => {
            this.refreshRegistrations$.next();
        });
    }

    pendingRegistrations(registrations: EventRegistration[]): EventRegistration[] {
        return registrations.filter((r) => r.status === 'pending');
    }

    activeRegistrations(registrations: EventRegistration[]): EventRegistration[] {
        return registrations.filter((r) => r.status === 'active');
    }

    waitlistRegistrations(registrations: EventRegistration[]): EventRegistration[] {
        return registrations.filter((r) => r.status === 'waitlist');
    }
}
